import {
  Component,
  Element,
  Event,
  State,
  type EventEmitter,
  Prop,
  Listen,
  Watch,
  Method,
} from '@stencil/core';
import {
  Strategies,
  StrategiesFactory,
} from '../../../../../strategies/Strategies';
import { makeLogger } from '../../../../../../logger/logger';
import type { SearchApi } from '../../../../../../api/SearchApi';
import {type PipeMiddlewareElement, vendorMiddlewares} from '../../../../../../utils/middleware-pipe/middleware-pipe';
import type { PipeDetailsAdditionalData } from '../../../../../../utils/middleware-pipe/models/middleware-pipe-details.model';
import {
  type MiddlewarePipeAdapterConfig,
  middlewareMisspellingsAdapater,
} from '../../../../../../utils/middleware-pipe/middleware-search-tab.adapater';
import type {
  ReflectedTab,
  SearchAutocorrectEvent,
  SearchCompletedEvent,
  SearchErrorEvent,
  SearchStartedEvent,
} from '../../../../../SearchCompletedEvent';
import { Subject, takeUntil } from 'rxjs';
import type { FacetsService } from '../../../../../../utils/services/facets/facets.service';
import type { PipesRegistry } from '../../../../../../utils/middleware-pipe/models/stack-pipes-registry.model';
import {ITEMS_PER_PAGE} from '../../../../../../constants/paginationConstants';
import {makeTabName} from '../../../util';

const { info, debug, warn, err } = makeLogger({
  pathToCurrentFile: 'components/result-page/search-tab/search-tab.tsx',
});

@Component({
  tag: 'dashboard-misspellings',
})
export class DashboardMisspellings {
  private currentPage = 1;
  private strategies: Strategies;
  private unsubscriber$: Subject<any> = new Subject<any>();//eslint-disable-line @typescript-eslint/no-explicit-any
  private facetService!: FacetsService;
  /**
  * Handle for setTimeout used in debounce mechanism
  * @private
  */
  private debounce: NodeJS.Timeout;
    @Prop() readonly country: string;
    @Prop() readonly language: string;
    @Prop() readonly timeout?: number;
    @Prop({mutable: true}) tabName: string;
    @Prop({mutable: true}) reflectedState:ReflectedTab;
    @Prop() readonly reflectedTab;
    @Prop() readonly tabTitle?: string;
    @Prop({mutable: true}) resultClickHandler?;
    @Prop() readonly perPage: number = ITEMS_PER_PAGE;

    @Prop({ attribute: 'apigee-auth' }) readonly apigeeAuth: string;
    @Prop({ attribute: 'apigee-token-url' }) readonly apigeeTokenUrl: string;
    @Prop({ attribute: 'token' }) readonly token: string;
    @Prop({ attribute: 'api-key' }) readonly apiKey: string;
    @Prop({ attribute: 'apigee-bearer' }) readonly apigeeBearer: string;
    @Prop({ attribute: 'api-endpoint' }) readonly apiEndpointProp: URL | string;

    @Prop({ attribute: 'query'}) readonly queryProp: string;

    @Prop() readonly middlewares: string;
    @Prop() readonly isAutocorrectEndpoint: boolean = false;
    @Prop() readonly runAutocorrect: boolean = false;
    @Prop({ attribute: 'preload-data' }) readonly preloadDataAttribute: string;
    @Prop() readonly isDashboardView: boolean = true;

    @State() skipAutocorrection?: boolean = false;
    @State() apiEndpoint: URL;
    @State() query = '';
    @State() queryResult: SearchApi | any;//eslint-disable-line @typescript-eslint/no-explicit-any
    @State() middlewareRegistry: PipesRegistry = {};

    @Element() el;

    @Watch('queryProp')
    queryUpdated(newValue, oldValue) {
        if (newValue !== oldValue) {
          this.query = newValue;
          this.reset();
        }
    }

    @Watch('preloadDataAttribute')
    preloadData() {
        try {
          const data = JSON.parse(this.preloadDataAttribute);
          this.reflectedState = {
            ...this.reflectedState,
            count: data?.count,
          };
          this.query = data?.query;
          this.queryResult = data?.queryResult;
        } catch (e) {
          err(
            'Preloading with fixed data - failed',
            e,
            this.preloadDataAttribute
          );
        }
    }

    @Method()
    @Listen('search:reset')
    async reset() {
        this.currentPage = 1;
        this.checkExistAndQueryCall();
    }

    private buildAdditionalPipeData(
      skipAutocorrection: boolean | undefined
    ): PipeDetailsAdditionalData | undefined {
        let additionalData: PipeDetailsAdditionalData | undefined;
        if (skipAutocorrection) {
          additionalData = { skipAutocorrection };
        }

        return additionalData;
    }

    private beforePipeMiddlewareActions(): void {
        this.reflectedState = { ...this.reflectedState, ajaxIsLoading: true };
        this.searchStarted.emit({ ...this.reflectedState, term: this.query });
    }

    @Method()
    async getMiddlewareRegistry() {
        return { ...vendorMiddlewares, ...this.middlewareRegistry };
    }

  @Method()
    async registerMiddleware(name: string, call: PipeMiddlewareElement<any, any>): Promise<void> { //eslint-disable-line @typescript-eslint/no-explicit-any
        this.middlewareRegistry[name] = call;
        return;
    }

  private onApiError(error: any) {//eslint-disable-line @typescript-eslint/no-explicit-any
      this.reflectedState = {
        ...this.reflectedState,
        ajaxIsLoading: false,
        hasError: true,
      };
      this.searchError.emit({
        ...this.reflectedState,
        errorStatus: error.status,
        errorCode: error.error,
        errorMessage: error.message,
        ajaxIsLoading: false,
      });

      err('returned error on ajax query!', error, this);
      this.queryResult = null;
  }

  private initPipeMiddleware(
    adapterConfig: MiddlewarePipeAdapterConfig,
    additionalData?: PipeDetailsAdditionalData
  ): void {
      this.beforePipeMiddlewareActions();
      middlewareMisspellingsAdapater<SearchApi>(
        this,
        adapterConfig,
        additionalData
      )
        .pipe(takeUntil(this.unsubscriber$))
        .subscribe({
          error: (error) => {
              this.onApiError(error);
          },
        });
  }

  private checkExistAndQueryCall(skipAutocorrection?): void {
      if (!skipAutocorrection) {
        skipAutocorrection = false;
      }

      if (this.apiEndpoint) {
        const additionalData: PipeDetailsAdditionalData =
                this.buildAdditionalPipeData(skipAutocorrection);
        this.initPipeMiddleware({}, additionalData);
        return;
      }
  }

  componentWillLoad() {
      if (!this.resultClickHandler) {
        this.resultClickHandler = () => {
            return true;
        };
      }


      if (!this.tabName) {
        this.tabName = makeTabName(this.tabTitle);
      }

      this.reflectedState = {
        tab: this.tabName,
        ajaxIsLoading: false,
        hasError: false,
        count: 0,
      };

      this.recalculateApiEndpoint(this.apiEndpointProp);
      this.strategies = StrategiesFactory.createStrategy(this.el);
  }

  componentDidLoad() {
      debug('Tab DidLoad', {
        tabTitle: this.tabTitle,
        query: this.query,
        queryProp: this.queryProp,
      });

      if (this.queryProp && this.apiEndpoint) {
        this.query = this.queryProp;
        if(!this.reflectedTab) this.checkExistAndQueryCall();
      }
      if (this.preloadDataAttribute) {
        this.preloadData();
      }
  }

    @Event({ eventName: 'search:started', bubbles: true, composed: true })
      searchStarted: EventEmitter<SearchStartedEvent>;
    @Event({ eventName: 'search:error' })
      searchError: EventEmitter<SearchErrorEvent>;
    @Event({ eventName: 'search:completed' })
      searchCompleted: EventEmitter<SearchCompletedEvent>;
    @Event({ eventName: 'search:autocorrect', bubbles: true, composed: true })
      searchAutocorrect: EventEmitter<SearchAutocorrectEvent>;
    @Event({
      eventName: 'search:SearchResultsStateEvent',
      composed: true,
      cancelable: true,
      bubbles: true,
    })
      SearchResultsStateEmitter: EventEmitter<any>;//eslint-disable-line @typescript-eslint/no-explicit-any

    @Event({
      eventName: 'search:SearchResultsEvent',
      composed: true,
      cancelable: true,
      bubbles: true,
    })
      SearchResultsEmitter: EventEmitter<any>;//eslint-disable-line @typescript-eslint/no-explicit-any

    @Watch('queryResult')
    updateCount(newValue /*, oldValue, trigger*/) {
        if (newValue?.searchResults?.totalHits) {
          this.reflectedState = {
            ...this.reflectedState,
            count: newValue?.searchResults?.totalHits,
          };
        }
        const {term, suggestion} = this.queryResult || {term:''};

        if (this.debounce !== null) {
          clearTimeout(this.debounce);
          this.debounce = null;
        }
        this.debounce = setTimeout(() => {
            this.SearchResultsStateEmitter.emit({
              reflectedState: {...this.reflectedState, term, suggestion},
            });
            this.debounce = null;
        }, 500);
        this.SearchResultsEmitter.emit({ queryResult: this.queryResult });
    }

    @Watch('apiEndpointProp')
    recalculateApiEndpoint(newValue: string | URL) {
        if (typeof newValue == 'string') {
          try {
            this.apiEndpoint = new URL(newValue as string, window.origin);
          } catch (e) {
            err(
              'Can\'t parse API endpoint as URL! Trying relevant path...',
              newValue
            );
          }
        } else {
          err('Wrong API format', newValue, typeof newValue);
        }
    }

    @Listen('search:autocorrectquery', { target: 'window' })
    searchAutocorrectQueryUpdate(event) {
        if (event.detail.changeQuery === true) {
          this.checkExistAndQueryCall(true);
        }
    }
}
